import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import {
  USER_SELECTABLE_WORKFLOW_STATUSES,
  WorkflowStatus,
} from '@enview/interface/types/triage/WorkflowStatus';
import { BillSummary } from '@enview/interface/types/bills/BillSummary';
import ArrowDown from '../../../components/svg/ArrowDown';
import WorkspacePicker from '../DropDowns/WorkspacePicker';
import { TeamAPI, BillTriageAPI } from '../../../api';
import { getOrgForTeamMode, getTeamMode } from '../../../dux';
import { State } from '../../../dux/@types';
import filteredTeamMembershipsForSelector, {
  getPrimaryWorkspaceId,
} from '../../../helpers/TeamHelper';
import { useToast } from '../Toast/ToastProvider';
import { singleBillStatusMessage } from '../BillTriage/workflowStatusToastMessages';
import useUndoWorkflowStatus from '../BillTriage/useUndoWorkflowStatus';
import '../DropDowns/DropDown.scss';
import './WorkflowStatusColumnField.scss';

const statusClass = (status: WorkflowStatus): string =>
  status.toLowerCase().replace(/\s+/g, '-');

interface WorkflowStatusColumnFieldProps {
  billId: string;
  billWorkspaces?: BillSummary['workspaces'];
  customFields?: BillSummary['customFields'];
}

const WorkflowStatusColumnField = (
  props: WorkflowStatusColumnFieldProps,
): ReactElement => {
  const { billId, billWorkspaces = [], customFields } = props;

  const organization = useSelector(getOrgForTeamMode);
  const orgId = organization?.id;
  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const currentTeamModeId = useSelector(getTeamMode);
  const { data: teamMemberships } =
    TeamAPI.endpoints.getUsersTeamMemberships.useQuery(undefined);
  const userWorkspaces = filteredTeamMembershipsForSelector(
    teamMemberships,
    orgUser,
    orgId,
  );

  const billWorkspaceIds = billWorkspaces
    .map((workspace) => workspace.id)
    .filter((id): id is number => id !== undefined);
  const teamId = getPrimaryWorkspaceId(
    userWorkspaces,
    billWorkspaceIds,
    currentTeamModeId,
  );

  const workflowStatusIn = (workspaceId: number): WorkflowStatus | undefined =>
    orgId
      ? (customFields?.[orgId]?.[workspaceId]?.['Workflow Status']?.fieldValue as
          | WorkflowStatus
          | undefined)
      : undefined;

  const status = teamId ? workflowStatusIn(teamId) : undefined;

  // The New Bills section matches a bill that is New Bill in any of the user's
  // workspaces, so changing it in one workspace only removes it from the section
  // when no other workspace still has it as New Bill.
  const leavesNewBillsQueue = (changedTeamId: number): boolean =>
    workflowStatusIn(changedTeamId) === WorkflowStatus.NewBill &&
    !userWorkspaces.some(
      (workspace) =>
        workspace.id !== changedTeamId &&
        workflowStatusIn(workspace.id) === WorkflowStatus.NewBill,
    );

  const { showToast } = useToast();
  const undoWorkflowStatus = useUndoWorkflowStatus();

  const [updateWorkflowStatus, { isLoading: statusUpdating }] =
    BillTriageAPI.endpoints.updateWorkflowStatus.useMutation();
  const [setWorkspaces, { isLoading: workspacesSaving }] =
    BillTriageAPI.endpoints.setBillWorkspaces.useMutation();
  const isMutating = statusUpdating || workspacesSaving;

  // A status picked while the bill isn't in any of the user's workspaces yet is
  // held here and applied once the forced-open WorkspacePicker below resolves a
  // workspace to write it to, instead of silently no-oping.
  const [pendingStatus, setPendingStatus] = useState<WorkflowStatus | null>(null);

  const [displayed, setDisplayed] = useState(false);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const updatePanelPosition = useCallback((): void => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPanelPosition({ top: rect.bottom + 4, left: rect.left });
  }, []);

  const handleToggle = (): void => {
    if (!displayed) updatePanelPosition();
    setDisplayed((prevDisplayed) => !prevDisplayed);
  };

  const handleClickOutside = useCallback((e: MouseEvent): void => {
    const target = e.target as Node;
    if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) {
      return;
    }
    setDisplayed(false);
  }, []);

  useEffect(() => {
    if (!displayed) return undefined;
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', updatePanelPosition, true);
    window.addEventListener('resize', updatePanelPosition);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updatePanelPosition, true);
      window.removeEventListener('resize', updatePanelPosition);
    };
  }, [displayed, handleClickOutside, updatePanelPosition]);

  const handleSelect = (value: WorkflowStatus): void => {
    setDisplayed(false);
    if (!orgId || value === status || isMutating) return;
    if (!teamId) {
      // No workspace to write the status against yet — gate behind picking one
      // instead of the previous silent no-op.
      setPendingStatus(value);
      return;
    }
    const previousStatus = status ?? null;
    const leftNewBillsQueue = leavesNewBillsQueue(teamId);
    void updateWorkflowStatus({ orgId, billId, teamId, status: value }).then(
      (result) => {
        if ('error' in result) return;
        showToast({
          message: singleBillStatusMessage({ status: value, leftNewBillsQueue }),
          onUndo: () =>
            undoWorkflowStatus({
              orgId,
              teamId,
              previousStatuses: [{ billId, status: previousStatus }],
            }),
        });
      },
    );
  };

  const handleSaveWorkspaces = async (workspaceIds: number[]): Promise<void> => {
    if (!orgId) return;
    await setWorkspaces({ orgId, billId, teamIds: workspaceIds }).unwrap();

    if (pendingStatus) {
      const newTeamId = getPrimaryWorkspaceId(
        userWorkspaces,
        workspaceIds,
        currentTeamModeId,
      );
      if (newTeamId) {
        const previousStatus = workflowStatusIn(newTeamId) ?? null;
        await updateWorkflowStatus({
          orgId,
          billId,
          teamId: newTeamId,
          status: pendingStatus,
        }).unwrap();
        setPendingStatus(null);
        const addedWorkspaceNames = userWorkspaces
          .filter(
            (workspace) =>
              workspaceIds.includes(workspace.id) &&
              !billWorkspaceIds.includes(workspace.id),
          )
          .map((workspace) => workspace.name);
        showToast({
          message: singleBillStatusMessage({
            status: pendingStatus,
            addedWorkspaceNames,
          }),
          onUndo: () =>
            undoWorkflowStatus({
              orgId,
              teamId: newTeamId,
              previousStatuses: [{ billId, status: previousStatus }],
              previousWorkspaces: { billId, teamIds: billWorkspaceIds },
            }),
        });
      }
    }
  };

  return (
    <div className="workflow-status-field">
      <div className="wsf-control">
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div
          className={`wsf-pill ${
            status ? `wsf-pill-${statusClass(status)}` : 'wsf-pill-unset'
          } ${isMutating ? 'wsf-pill-disabled' : ''}`}
          onClick={isMutating ? undefined : handleToggle}
          ref={triggerRef}
        >
          <span className="wsf-pill-label">{status || 'Set status'}</span>
          <ArrowDown className="wsf-pill-caret" />
        </div>

        {displayed &&
          createPortal(
            // "show" opts the options into the shared DropDown.scss item styles, matching
            // SingleSelectDropDown; the panel itself stays a fixed-position portal so it
            // isn't clipped by the table.
            <div
              className="wsf-dropdown-panel show"
              ref={panelRef}
              style={{
                position: 'fixed',
                top: panelPosition.top,
                left: panelPosition.left,
              }}
            >
              {USER_SELECTABLE_WORKFLOW_STATUSES.map((option) => (
                <button
                  className={`dropdown-item ${status === option ? 'checked' : ''} ${
                    isMutating ? 'wsf-dropdown-item-disabled' : ''
                  }`}
                  disabled={isMutating}
                  key={option}
                  onClick={() => handleSelect(option)}
                  type="button"
                >
                  <div className="dropdown-item-text">{option}</div>
                </button>
              ))}
            </div>,
            document.body,
          )}
      </div>

      <WorkspacePicker
        billWorkspaceIds={billWorkspaceIds}
        forceOpen={!!pendingStatus}
        onForceClose={() => setPendingStatus(null)}
        onSave={handleSaveWorkspaces}
        requireSelection
        saving={isMutating}
        sectionLabel={
          pendingStatus
            ? `SELECT A WORKSPACE TO MARK AS ${pendingStatus.toUpperCase()}...`
            : 'APPLY WORKFLOW STATUS IN...'
        }
        trigger={<ArrowDown className="wsf-workspace-caret" />}
        workspaces={userWorkspaces}
      />
    </div>
  );
};

export default WorkflowStatusColumnField;
